import type { FC } from 'react';
import { _isNotBlank } from '@/littledash';
import { RouteService } from '@/support/RouteService';
import { DateTimeRenderFormat, DateUtils } from '@/utils/Date.utils';
import { ModalActions, ModalContainer, ModalHeader } from '@/utils/modal';
import APITable from '@/components/UI/Table/Reusable/APITable/APITable';
import type { TanStackColumns } from '@/components/UI/Table/TableComponent.model';
import UserAvatar from '@/components/UI/UserAvatar/UserAvatar';
import { auditActionLabel, auditEntityLabel } from './AuditHistory.utils';
import type { SyncConfigurationAuditChange } from '../SyncConfiguration.model';
import styles from './SyncConfigurationAuditModal.module.scss';

const PLACEHOLDER = '-';

const withPlaceholder = (value: string | null): string => (_isNotBlank(value) ? value : PLACEHOLDER);

const AUDIT_ROUTE = RouteService.api({ endpoint: 'GET /api/v1/team/sync-configuration/audit', path: undefined }).url;

type CellProps = { row: { original: SyncConfigurationAuditChange } };

// Column ids double as the server's sort keys, so only created_at and created_by are sortable.
const COLUMNS: TanStackColumns<SyncConfigurationAuditChange> = [
  {
    id: 'created_at',
    header: 'Timestamp (UTC)',
    accessorKey: 'created_at',
    isVisible: true,
    size: 200,
    enableSorting: true,
    cell: ({ row: { original } }: CellProps) =>
      DateUtils.renderDateTime(original.created_at, { timezone: 'UTC', format: DateTimeRenderFormat.ISODateTimeUTC }),
  },
  {
    id: 'created_by',
    header: 'User',
    accessorKey: 'created_by',
    isVisible: true,
    // Usually one avatar, so sized to its header rather than sharing spare width.
    size: 80,
    maxSize: 80,
    enableSorting: true,
    cell: ({ row: { original } }: CellProps) => {
      const name = original.created_by?.name ?? 'System';
      return <UserAvatar user={{ name }} tooltip={name} />;
    },
  },
  {
    id: 'action',
    header: 'Action',
    accessorKey: 'action',
    isVisible: true,
    enableSorting: false,
    cell: ({ row: { original } }: CellProps) => auditActionLabel(original.action),
  },
  {
    id: 'entity_type',
    header: 'Entity',
    accessorKey: 'entity_type',
    isVisible: true,
    enableSorting: false,
    cell: ({ row: { original } }: CellProps) => withPlaceholder(auditEntityLabel(original.entity_type)),
  },
  {
    id: 'attribute',
    header: 'Item',
    accessorKey: 'attribute',
    isVisible: true,
    enableSorting: false,
    cell: ({ row: { original } }: CellProps) => withPlaceholder(original.attribute),
  },
  {
    id: 'old_value',
    header: 'Old value',
    accessorKey: 'old_value',
    isVisible: true,
    enableSorting: false,
    cell: ({ row: { original } }: CellProps) => withPlaceholder(original.old_value),
  },
  {
    id: 'new_value',
    header: 'New value',
    accessorKey: 'new_value',
    isVisible: true,
    enableSorting: false,
    cell: ({ row: { original } }: CellProps) => withPlaceholder(original.new_value),
  },
];

export interface SyncConfigurationAuditModalProps {
  closeModal: () => void;
}

/**
 * The team's sync configuration audit history, one row per changed attribute, laid out like the
 * study audit log. Paged and sorted by the server.
 */
const SyncConfigurationAuditModal: FC<SyncConfigurationAuditModalProps> = ({ closeModal }) => (
  <ModalContainer size="wide" testKey="sync-configuration-audit-modal">
    <ModalHeader title="Configuration Audit History" closeModal={closeModal} className="pa3 bb b--moon-gray bg-white" />

    <div
      className={styles.tableBody}
      data-test-component="SyncConfigurationAuditModal"
      data-testid="sync-configuration-audit"
    >
      <APITable<SyncConfigurationAuditChange>
        apiInfo={{ type: 'internalApi', route: AUDIT_ROUTE }}
        reduxTableName="syncConfigurationAudit"
        columns={COLUMNS}
        defaultSortBy={{ id: 'created_at', desc: true }}
        pageSizes={[25, 50, 100]}
        defaultPageSize={25}
        noDataMessage="No configuration changes yet"
        testIdPrefix="sync-configuration-audit"
        hideSearch
        hideFilter
        hideRefreshButton
      />
    </div>

    <div className="pa3 bt b--moon-gray">
      <ModalActions onCancel={closeModal} cancelBtnText="Close" />
    </div>
  </ModalContainer>
);

export default SyncConfigurationAuditModal;
